<div class="notifications-main-container" #notificationsMainWindow tabindex="-1" role="dialog" [attr.aria-modal]="true">
  <div class="notifications-list" #notificationsList>
    <div class="list-header">
      <div class="title">
        <h1>{{ 'IN_APP_NOTIFICATIONS.NOTIFICATIONS' | translate }}</h1>
        <button
          class="close-icon"
          type="button"
          (click)="closeNotifications.emit()"
          [attr.aria-label]="'COMMON.ACTION.CLOSE' | translate">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <div class="buttons-wrapper">
      <button
        class="standard-form-button"
        [class.button-toggled]="!showOnlyUnread"
        type="button"
        [attr.aria-pressed]="!showOnlyUnread"
        (click)="setShowOnlyUnread(false)">
        {{ 'IN_APP_NOTIFICATIONS.ALL' | translate }}
      </button>

      <button
        class="standard-form-button"
        [class.button-toggled]="showOnlyUnread"
        type="button"
        [attr.aria-pressed]="showOnlyUnread"
        (click)="setShowOnlyUnread(true)">
        {{ 'IN_APP_NOTIFICATIONS.UNREAD' | translate }}
      </button>
      <button
        class="mark-all-button"
        type="button"
        mat-icon-button
        [matTooltip]="'IN_APP_NOTIFICATIONS.MARK_ALL_AS_READ' | translate"
        matTooltipPosition="below"
        [attr.aria-label]="'IN_APP_NOTIFICATIONS.MARK_ALL_AS_READ' | translate"
        (click)="markAllAsRead()">
        <mat-icon fontSet="material-icons-outlined" aria-hidden="true">mark_email_read</mat-icon>
      </button>
    </div>

    @if (loading$ | async) {
      <div class="loading-wrapper" role="status" aria-live="polite">
        <span class="sr-only">
          {{ 'COMMON.LAYOUT.LOADING' | translate }}
        </span>
        <mat-spinner [diameter]="55" [attr.aria-hidden]="true"></mat-spinner>
      </div>
    }
    @if (shortlist?.length) {
      @for (
        notification of shortlist;
        track trackByFn(notificationIndex, notification);
        let notificationIndex = $index
      ) {
        <div
          class="notification-item"
          [class.not-read]="!notification.read"
          [class.not-clickable]="!notification.clickable">
          @if (notification.linkParams) {
            <a
              class="notification-with-link-params link-without-style"
              [routerLink]="notification.linkParams.linkParams"
              [queryParams]="notification.linkParams.extras?.queryParams">
              <ng-container
                [ngTemplateOutlet]="notificationContent"
                [ngTemplateOutletContext]="{ notification: notification }">
              </ng-container>
            </a>
          }
          @if (!notification.linkParams) {
            <div
              class="notification-without-link-params"
              role="button"
              tabindex="0"
              [attr.aria-disabled]="!notification.clickable"
              [attr.aria-labelledby]="'notification-label-' + notification.id"
              (click)="notificationClick(notification, $event)">
              <ng-container
                [ngTemplateOutlet]="notificationContent"
                [ngTemplateOutletContext]="{ notification: notification }">
              </ng-container>
            </div>
          }
        </div>
      }
    } @else {
      <div class="no-notifications-message">
        <h1>{{ 'IN_APP_NOTIFICATIONS.NO_NOTIFICATIONS' | translate }}</h1>
      </div>
    }
    @if (loadingMore$ | async) {
      <div class="loading-more" role="status" aria-live="polite">
        <span class="sr-only">
          {{ 'COMMON.LAYOUT.LOADING' | translate }}
        </span>
        <mat-spinner class="loading-more-spinner" [diameter]="35" [attr.aria-hidden]="true"></mat-spinner>
      </div>
    }
    @if (allNotificationsLoaded) {
      <div class="no-more-notifications">
        <p>{{ 'IN_APP_NOTIFICATIONS.NO_MORE_NOTIFICATIONS' | translate }}</p>
      </div>
    }
  </div>
</div>

<ng-template #notificationContent let-notification="notification">
  <button
    class="row content-row"
    type="button"
    (click)="notificationClick(notification, $event)"
    [attr.aria-labelledby]="'notification-label-' + notification.id"
    [matTooltip]="
      !notification.clickable
        ? ('IN_APP_NOTIFICATIONS.YOU_CANT_SEE_DETAILS_BECAUSE_WRONG_PROCESS_STATUS'
          | translate
            : {
                foreignerName: notification.foreignerName,
                document: notification.relatedUserDocument?.document,
                relatedUserProcess: notification.relatedUserProcess,
              })
        : null
    "
    matTooltipClass="universalTooltip"
    [matTooltipDisabled]="notification.clickable || myself?.role?.key !== ROLES_KEYS.Foreigner"
    matTooltipPosition="left">
    <!-- LABEL FOR SCREEN READER -->
    <div [id]="'notification-label-' + notification.id" class="sr-only">
      {{
        'IN_APP_NOTIFICATIONS.' + notification.type
          | translate
            : {
                foreignerName: notification.foreignerName,
                document: notification.relatedUserDocument?.document,
                documentName: notification.documentName,
                relatedUserProcess: notification.relatedUserProcess,
                origError: notification.origError,
                externalSystemName: notification.data?.additionalInfo?.mergingJobOpts?.externalSystemName || null,
                signingDate: notification.data?.additionalInfo?.signingDate,
                signingMode: notification.data?.additionalInfo?.signingMode,
                additionalInfo: notification.data?.additionalInfo,
              }
          | stripHtml
      }}
    </div>

    <!-- NOTIFICATIONS -->
    <div class="column column-icon" [class.multi-icons]="notification.icons?.length > 1">
      @for (icon of notification.icons; track icon) {
        <mat-icon [fontSet]="icon.fontSet || null" [ngClass]="icon.className">
          {{ icon.name }}
        </mat-icon>
      }
    </div>
    <div class="column column-text">
      <p
        class="text notification"
        [innerHTML]="
          'IN_APP_NOTIFICATIONS.' + notification.type
            | translate
              : {
                  foreignerName: notification.foreignerName,
                  document: notification.relatedUserDocument?.document,
                  documentName: notification.documentName,
                  relatedUserProcess: notification.relatedUserProcess,
                  origError: notification.origError,
                  externalSystemName: notification.data?.additionalInfo?.mergingJobOpts?.externalSystemName || null,
                  signingDate: notification.data?.additionalInfo?.signingDate,
                  signingMode: notification.data?.additionalInfo?.signingMode,
                  additionalInfo: notification.data?.additionalInfo,
                }
            | sanitizeHtml
            | truncate: 140 : false
        "></p>
    </div>
    <div class="column column-read-indicator">
      @if (loadingChangesIn !== notification.id) {
        <div
          [matBadgeHidden]="!notification.stacked"
          matBadgePosition="before"
          matBadgeSize="medium"
          [matBadgeColor]="notification.read ? 'accent' : 'warn'"
          [matBadge]="notification.stackSize"
          class="unread-icon"
          [class.hide-dot]="notification.read || notification.stacked"></div>
      } @else {
        <div class="loading-wrapper" role="status" aria-live="polite">
          <span class="sr-only">
            {{ 'COMMON.LAYOUT.LOADING' | translate }}
          </span>
          <mat-spinner [diameter]="15" [attr.aria-hidden]="true"></mat-spinner>
        </div>
      }
    </div>
  </button>

  <div class="meta-wrapper">
    @if (!notification.read) {
      <button
        class="action-button"
        type="button"
        (click)="$event.stopPropagation(); $event.preventDefault(); markAsRead(notification)">
        {{ 'IN_APP_NOTIFICATIONS.MARK_AS_READ' | translate }}
      </button>
    }
    @if (notification.read) {
      <button
        class="action-button"
        type="button"
        (click)="$event.stopPropagation(); $event.preventDefault(); markAsUnread(notification)">
        {{ 'IN_APP_NOTIFICATIONS.MARK_AS_UNREAD' | translate }}
      </button>
    }
    <p class="date">{{ notification.createdAt | date: 'dd-MM-yyyy HH:mm' }}</p>
  </div>
</ng-template>
